import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';
import './ContactFormModal.css';

function ContactFormModal({ isOpen, onClose }) {
  const { user } = useAuth();
  const [topic, setTopic] = useState('Feedback');
  const [description, setDescription] = useState('');
  const [files, setFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (description.trim().length < 10) {
      setSubmitError('Please provide at least 10 characters in your description');
      return;
    }

    if (description.length > 5000) {
      setSubmitError('Description must not exceed 5000 characters');
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Create FormData to send files with the request
      const formData = new FormData();
      formData.append('topic', topic);
      formData.append('description', description);

      // Append each file
      files.forEach((file) => {
        formData.append('files', file);
      });

      await api.uploadFile('/submit-feedback', formData);

      setSubmitSuccess(true);

      // Auto-close after 2 seconds
      setTimeout(() => {
        resetForm();
        onClose();
      }, 2000);

    } catch (error) {
      console.error('Submission error:', error);
      setSubmitError(error.response?.data?.error || 'Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setTopic('Feedback');
    setDescription('');
    setFiles([]);
    setSubmitError(null);
    setSubmitSuccess(false);
    setIsDragging(false);
  };

  // Handle file selection
  const handleFileSelect = (selectedFiles) => {
    // Validate file count
    if (files.length + selectedFiles.length > 3) {
      setSubmitError('Maximum 3 files allowed');
      return;
    }

    // Allowed file types
    const ALLOWED_TYPES = [
      'image/png',
      'image/jpeg',
      'image/gif',
      'image/webp',
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];

    const validFiles = [];
    for (const file of selectedFiles) {
      // Validate type
      if (!ALLOWED_TYPES.includes(file.type)) {
        setSubmitError(`${file.name} is not an allowed file type. Allowed: PNG, JPG, GIF, WebP, PDF, DOC, DOCX`);
        continue;
      }

      // Validate size (10MB)
      if (file.size > 10 * 1024 * 1024) {
        setSubmitError(`${file.name} exceeds 10MB limit`);
        continue;
      }

      validFiles.push(file);
    }

    // Add valid files to state
    if (validFiles.length > 0) {
      setFiles(prev => [...prev, ...validFiles]);
      setSubmitError(null);
    }
  };

  // Remove file
  const removeFile = (fileName) => {
    setFiles(prev => prev.filter(f => f.name !== fileName));
  };

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    handleFileSelect(droppedFiles);
  };

  // Format file size for display
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  // Handle ESC key
  React.useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && isOpen && !isSubmitting) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, isSubmitting, onClose]);

  if (!isOpen) return null;

  // Portal to <body> so the modal escapes the sidebar's stacking context and
  // covers everything (incl. the fixed floating chatbot button).
  return createPortal(
    <div className="contact-form-overlay" onClick={onClose}>
      <div className="contact-form-container" onClick={(e) => e.stopPropagation()}>
        {/* Close button */}
        <button className="contact-form-close" onClick={onClose} type="button">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#696969" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>

        {/* Header */}
        <div className="contact-form-header">
          <h2>Contact Us</h2>
          <p>We'll respond as soon as we can.</p>
        </div>

        {/* Success State */}
        {submitSuccess ? (
          <div className="contact-form-success">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <path d="M9 12l2 2 4-4"/>
            </svg>
            <h3>Thank you!</h3>
            <p>Your message has been sent successfully.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {/* Topic Dropdown */}
            <div className="contact-form-field">
              <label htmlFor="topic">Topic</label>
              <select
                id="topic"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                disabled={isSubmitting}
              >
                <option value="Feedback">Feedback</option>
                <option value="Issue">Issue</option>
              </select>
            </div>

            {/* Description Textarea */}
            <div className="contact-form-field">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe your issue/feedback here. Please try to be as detailed as possible."
                disabled={isSubmitting}
                rows={6}
              />
            </div>

            {/* File Upload Area */}
            <div className="contact-form-field">
              <label>Attach photo/video</label>
              <div
                className={`contact-form-upload ${isDragging ? 'dragging' : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".png,.jpg,.jpeg,.gif,.webp,.pdf,.doc,.docx"
                  onChange={(e) => handleFileSelect(Array.from(e.target.files || []))}
                  style={{ display: 'none' }}
                />
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="1.5">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="17 8 12 3 7 8"/>
                  <line x1="12" y1="3" x2="12" y2="15"/>
                </svg>
                <p>Drop your files here</p>
                <span>or click to browse</span>
              </div>
            </div>

            {/* File List */}
            {files.length > 0 && (
              <div className="contact-form-files">
                {files.map(file => (
                  <div key={file.name} className="contact-form-file-item">
                    <div className="file-info">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2">
                        <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"/>
                        <polyline points="13 2 13 9 20 9"/>
                      </svg>
                      <span className="file-name">{file.name}</span>
                      <span className="file-size">{formatFileSize(file.size)}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeFile(file.name)}
                      className="file-remove-btn"
                      disabled={isSubmitting}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Error Message */}
            {submitError && (
              <div className="contact-form-error">
                {submitError}
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              className="contact-form-submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <div className="submit-spinner"></div>
                  <span>Sending...</span>
                </>
              ) : (
                <>
                  <span>Send message</span>
                  <svg width="20" height="22" viewBox="0 0 20 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M18.7367 9.56518L2.33046 0.200925C2.05372 0.0451373 1.73609 -0.022627 1.41987 0.00665962C1.10365 0.0359463 0.803861 0.160893 0.560443 0.364855C0.317026 0.568816 0.141537 0.842107 0.0573564 1.14832C-0.0268245 1.45453 -0.0157008 1.77912 0.089246 2.07885L3.08046 10.9324L0.089246 19.7869C0.00600142 20.0225 -0.0195171 20.2746 0.0148315 20.5221C0.0491801 20.7697 0.142394 21.0053 0.286651 21.2094C0.430909 21.4134 0.622002 21.5799 0.843896 21.6948C1.06579 21.8097 1.31201 21.8698 1.5619 21.8699C1.83339 21.8693 2.10013 21.7987 2.33632 21.6648L18.7348 12.2849C18.9766 12.1494 19.1781 11.952 19.3185 11.7129C19.4589 11.4739 19.5331 11.2018 19.5336 10.9245C19.5341 10.6473 19.4608 10.3749 19.3213 10.1353C19.1818 9.89576 18.981 9.69763 18.7396 9.56128L18.7367 9.56518ZM1.5619 20.3074C1.56232 20.3035 1.56232 20.2995 1.5619 20.2957L4.4662 11.7136H10.1557C10.3629 11.7136 10.5616 11.6313 10.7081 11.4848C10.8546 11.3383 10.9369 11.1396 10.9369 10.9324C10.9369 10.7252 10.8546 10.5265 10.7081 10.3799C10.5616 10.2334 10.3629 10.1511 10.1557 10.1511H4.4662L1.56776 1.57299C1.5668 1.56747 1.56481 1.56217 1.5619 1.55737L17.9682 10.9158L1.5619 20.3074Z" fill="white"/>
                  </svg>
                </>
              )}
            </button>
          </form>
        )}
      </div>
    </div>,
    document.body
  );
}

export default ContactFormModal;
